import React, { createContext, useContext, useState, useEffect } from "react";
import { getToken } from "../api/client";
import * as authApi from "../api/auth";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Authenticated user: { id, email, full_name, role: "Professeur"|"Etudiant", is_admin }
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
  }, [isDarkMode]);

  // Hydrate the session from a stored token on first load.
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!getToken()) {
        setLoadingAuth(false);
        return;
      }
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(me);
      } catch (_) {
        /* invalid/expired token — client already cleared it */
      } finally {
        if (!cancelled) setLoadingAuth(false);
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  const login = async (email, password) => {
    const me = await authApi.login(email, password);
    setUser(me);
    return me;
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  return (
    <AppContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        user,
        loadingAuth,
        login,
        logout,
        // Derived for backward compatibility: every existing `userRole` /
        // `professorName` read site keeps working without changes.
        userRole: user?.role,
        professorName: user?.full_name,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

export default AppContext;
